import * as React from "react";
import { css, cx } from "@emotion/css";

interface PfSwitchButtonPropsControlled {
  isOn?: boolean;
  onClick: React.MouseEventHandler;
  onKeydown: React.KeyboardEventHandler;
  defaultState?: boolean;
  id: string;
}

interface PfSwitchButtonPropsUncontrolled {
  isOn?: boolean;
  onClick?: React.MouseEventHandler;
  onKeydown?: React.KeyboardEventHandler;
  defaultState: boolean;
  id: string;
}

type PfSwitchButtonProps =
  | PfSwitchButtonPropsControlled
  | PfSwitchButtonPropsUncontrolled;

/**
 * @param isOn boolean value which is the only state of this component. Pass this parameter make component controlled.
 * @param onClick Event handler, if isOn is passed, then onClick is also required.
 * @param onKeydown Event handler, if isOn is passed, then onClick is also required.
 * @param defaultState default value of this component if it's uncontrolled.
 */
export const PfSwitchButton = ({
  id,
  isOn,
  onClick,
  onKeydown,
  defaultState = true
}: PfSwitchButtonProps) => {
  const switchButtonWrapperClass = css`
    display: inline-block;
    background: #d7d7d7;
    height: 32px;
    box-sizing: border-box;
    position: relative;
    font-size: 0;
    cursor: pointer;
    text-transform: uppercase;
  `;

  const switchButtonYesClass = css`
    display: inline-block;
    width: 50px;
    height: 32px;
    text-align: center;
    font-size: 16px;
    line-height: 32px;
    background: #0083c1;
    color: #fff;
    font-weight: 600;
    border: 1px solid #ccc;
    text-shadow: 1px 1px 0 #555;
    user-select: none;
  `;

  const switchButtonNoClass = css`
    display: inline-block;
    width: 50px;
    height: 32px;
    text-align: center;
    font-size: 16px;
    line-height: 32px;
    font-weight: 600;
    border: 1px solid #ccc;
    user-select: none;
  `;

  const switchButtonSliderClass = css`
    position: absolute;
    top: 0;
    background: #fff;
    border: 1px solid #ccc;
    width: 50px;
    height: 32px;
    transition: 0.2s;
  `;

  const switchButtonIsOnClass = css`
    transform: translateX(50px);
  `;

  // if this component is not controlled, use its own state.
  const [internalIsOn, setInternalIsOn] = React.useState(defaultState);

  const internalClickHandler = () => {
    setInternalIsOn(!internalIsOn);
  };

  const internalKeyHandler = () => {
    setInternalIsOn(!internalIsOn);
  };

  //
  const currentState = isOn === undefined ? internalIsOn : isOn;
  const clickHandler = isOn === undefined ? internalClickHandler : onClick;
  const keyDownHandler = isOn === undefined ? internalKeyHandler : onKeydown;
  return (
    <div
      data-testid={id}
      role="button"
      tabIndex={0}
      onClick={clickHandler}
      onKeyDown={keyDownHandler}
      className={switchButtonWrapperClass}
    >
      <div className={switchButtonYesClass}>Yes</div>
      <div className={switchButtonNoClass}>No</div>
      <div
        className={cx(switchButtonSliderClass, {
          [switchButtonIsOnClass]: currentState
        })}
      />
    </div>
  );
};
